import { useState, useEffect } from 'react';
import { getRefundQueue, completeRefund, type RefundItem } from '../api/client';

// Manual refund queue (FR-026 / User Story 6). Paid-but-unsuccessful sessions
// an operator must refund by hand (Stripe for online, cash for cash), then
// mark done here.
export function RefundsPage() {
  const [items, setItems] = useState<RefundItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setItems(await getRefundQueue());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load refund queue');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleComplete(item: RefundItem) {
    const how = item.method === 'cash' ? 'cash' : 'Stripe';
    if (!confirm(`Mark refunded? Confirm you returned ${item.owedCzk} Kč via ${how} for code ${item.code ?? '—'}.`)) {
      return;
    }
    setBusyId(item.sessionId);
    try {
      await completeRefund(item.sessionId);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Mark-refunded failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>Refunds</h2>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="card">
        <div className="card-header">Awaiting manual refund ({items.length})</div>
        <div className="card-body">
          {loading && <div className="empty-state"><p className="text-muted">Loading…</p></div>}
          {error && <div className="login-error">{error}</div>}
          {!loading && !error && items.length === 0 && (
            <div className="empty-state"><p className="text-muted">Nothing to refund. 🎉</p></div>
          )}
          {!loading && items.length > 0 && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reason</th>
                    <th>Machine</th>
                    <th>Paid</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.sessionId}>
                      <td style={{ fontFamily: 'monospace' }}>{it.code ?? '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{it.owedCzk} Kč</td>
                      <td>
                        <span className={`badge ${it.method === 'cash' ? 'badge--warning' : 'badge--online'}`}>
                          {it.method ?? '—'}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge--offline">{it.state}</span>
                      </td>
                      <td>{it.machineId ?? '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {it.paidAt ? new Date(it.paidAt).toLocaleString() : '—'}
                      </td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={busyId === it.sessionId}
                          onClick={() => handleComplete(it)}
                        >
                          {busyId === it.sessionId ? 'Saving…' : 'Mark refunded'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
